import React, { useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button, Input, Badge, formatLocale } from '@plerionhq/design-system';
import {
  useTenantLimits,
  useSetTenantLimit,
  useDeleteTenantLimit,
  useOrgTenants,
  useOrganizationUsageQuery,
  type TenantLimitConfig,
} from '../../../../api/pleri-usage';
import { getPeriodForMonth } from './UsageDashboard';
import { LoadingSpinner } from '../ToolExecution/LoadingSpinner';

import { useTranslation } from 'react-i18next';

function formatTokenCount(n: number): string {
  if (n >= 1_000_000) return `${parseFloat((n / 1_000_000).toFixed(1))}M tokens`;
  return `${n.toLocaleString(formatLocale())} tokens`;
}

interface TenantRow {
  tenantId: string;
  name: string;
  usedTokens: number | null;
  limitTokens: number | null;
}

export const TenantLimitsTab: React.FC = () => {
  const { t } = useTranslation('pleri/settings');
  const now = useMemo(() => new Date(), []);
  const period = useMemo(
    () => getPeriodForMonth(now.getFullYear(), now.getMonth() + 1),
    [now],
  );

  const { data: orgTenants, isLoading: tenantsLoading } = useOrgTenants();
  const { data: tenantLimits, isLoading: limitsLoading } = useTenantLimits();

  const usageParams = useMemo(
    () => ({
      startDate: period.start,
      endDate: period.end,
      groupBy: 'tenantId',
    }),
    [period.start, period.end],
  );
  const { data: usageData, isLoading: usageLoading } = useOrganizationUsageQuery(
    usageParams,
    true,
  );

  const usageByTenant = useMemo(() => {
    const map = new Map<string, number>();
    if (!usageData?.data || !Array.isArray(usageData.data)) return map;
    for (const row of usageData.data) {
      if (row.tenantId != null && row.tenantId !== '') {
        map.set(row.tenantId, (map.get(row.tenantId) ?? 0) + row.totalTokens);
      }
    }
    return map;
  }, [usageData?.data]);

  const limitByTenant = useMemo(() => {
    const map = new Map<string, number | null>();
    if (!tenantLimits) return map;
    for (const lim of tenantLimits as TenantLimitConfig[]) {
      map.set(lim.tenantId, lim.limitTokens);
    }
    return map;
  }, [tenantLimits]);

  const rows = useMemo((): TenantRow[] => {
    if (!orgTenants) return [];
    return orgTenants.map((t) => ({
      tenantId: t.tenantId,
      name: t.name,
      usedTokens: usageByTenant.get(t.tenantId) ?? null,
      limitTokens: limitByTenant.get(t.tenantId) ?? null,
    }));
  }, [orgTenants, usageByTenant, limitByTenant]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTokens, setDraftTokens] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const setLimitMutation = useSetTenantLimit();
  const deleteLimitMutation = useDeleteTenantLimit();
  const isPending = setLimitMutation.isPending || deleteLimitMutation.isPending;

  const startEdit = (row: TenantRow) => {
    setEditingId(row.tenantId);
    setDraftTokens(row.limitTokens);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftTokens(null);
    setSaveError(null);
  };

  const saveRow = (row: TenantRow) => {
    setSaveError(null);
    const newTokens = draftTokens != null && draftTokens > 0 ? Math.round(draftTokens) : null;

    if (newTokens === null && row.limitTokens !== null) {
      deleteLimitMutation.mutate(row.tenantId, {
        onSuccess: cancelEdit,
        onError: () => setSaveError(t('failedToRemoveLimitPleaseTryAgain')),
      });
    } else if (newTokens !== null) {
      setLimitMutation.mutate(
        { tenantId: row.tenantId, limitTokens: newTokens },
        {
          onSuccess: cancelEdit,
          onError: () => setSaveError(t('failedToSaveLimitPleaseTryAgain')),
        },
      );
    } else {
      cancelEdit();
    }
  };

  const isLoading = tenantsLoading || limitsLoading || usageLoading;

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <LoadingSpinner text={t('loadingTenantLimits')} />
      </div>
    );
  }

  if (!orgTenants || orgTenants.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        {t('noTenantsFound')}
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-x-hidden rounded-md border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900">
      {/* Header */}
      <div className="grid grid-cols-12 bg-gray-100 dark:bg-gray-800">
        <div className="col-span-3 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('common:tenant')}
        </div>
        <div className="col-span-2 px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('usedThisMonth')}
        </div>
        <div className="col-span-5 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('monthlyLimit')}
        </div>
        <div className="col-span-2 px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('actions')}
        </div>
      </div>
      {/* Rows */}
      {rows.map((row, idx) => {
        const isEditing = editingId === row.tenantId;

        return (
          <div
            key={row.tenantId}
            className={`grid grid-cols-12 ${isEditing ? 'items-start' : 'items-center'} ${
              idx < rows.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''
            }`}
          >
            {/* Tenant */}
            <div className="col-span-3 px-4 py-3">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {row.name}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{row.tenantId}</div>
            </div>
            {/* Used this month */}
            <div className="col-span-2 px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
              {row.usedTokens != null ? (
                formatTokenCount(row.usedTokens)
              ) : (
                <span className="text-gray-400 dark:text-gray-500">—</span>
              )}
            </div>
            {isEditing ? (
              <div className="col-span-7 flex items-start gap-2 px-4 py-3">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      className="w-[160px]"
                      placeholder="e.g. 1000000"
                      value={draftTokens ?? ''}
                      disabled={isPending}
                      onChange={(e) => {
                        const v = e.target.valueAsNumber;
                        setDraftTokens(isNaN(v) ? null : v);
                      }}
                    />
                    <span className="shrink-0 text-sm text-gray-500 dark:text-gray-400">
                      {t('tokensUnit')}
                    </span>
                    {row.limitTokens != null && (
                      <button
                        type="button"
                        title={t('resetToUnlimited')}
                        disabled={isPending}
                        className="rounded p-1 text-gray-400 hover:text-gray-700 disabled:opacity-40 dark:text-gray-500 dark:hover:text-gray-200"
                        onClick={() =>
                          deleteLimitMutation.mutate(row.tenantId, { onSuccess: cancelEdit })
                        }
                      >
                        <RotateCcw size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="ml-auto flex shrink-0 flex-col items-end gap-1">
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      label={t('common:cancel')}
                      size="small"
                      isDisabled={isPending}
                      onClick={cancelEdit}
                    />
                    <Button
                      label={t('common:save')}
                      variant="primary"
                      size="small"
                      isDisabled={isPending}
                      onClick={() => saveRow(row)}
                    />
                  </div>
                  {saveError && (
                    <p className="text-xs text-red-500">{saveError}</p>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="col-span-5 px-4 py-3">
                  {row.limitTokens != null ? (
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatTokenCount(row.limitTokens)}
                    </span>
                  ) : (
                    <Badge label={t('noLimit')} />
                  )}
                </div>

                <div className="col-span-2 flex items-center justify-end px-4 py-3">
                  <Button
                    variant="primary"
                    label={t('common:edit')}
                    size="small"
                    isDisabled={editingId !== null}
                    onClick={() => startEdit(row)}
                  />
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
